import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, useMemo, useRef } from 'react';

import { CallActionsPanelButton } from '@gem/call/components/CallActionsPanelButton/CallActionsPanelButton';
import {
  IcBackgroundEffectIcon,
  IcDoneIcon,
  IcReportFilled24Icon,
  IcVideoOff24Icon,
  IcVideoOn24Icon,
  IcVoiceOff24Icon,
  IcVoiceOn24Icon,
} from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { IconView, IconViewSizes, PopoverMenu, IPopoverMenuItem } from '@gem/ui-kit';

import { usePreviewActionsPanelPresenter } from './PreviewActionsPanel.presenter';
import styles from './PreviewActionsPanel.styles.m.css';

interface IPreviewActionsPanelProps {
  isAudioMuted: boolean;
  isVideoMuted: boolean;
  audioOutputDeviceId: string | null;
  audioInputDeviceId: string | null;
  videoInputDeviceId: string | null;
  videoPermissionDisabled: boolean;
  audioPermissionDisabled: boolean;
  canShowBackgroundPanel: boolean;
  setCurrentAudioInput(deviceId: string, needToPublish?: boolean): void;
  setCurrentVideoInput(deviceId: string, needToPublish?: boolean): void;
  setCurrentAudioOutput(deviceId: string, needToPublish?: boolean): void;
  unmuteAudio(): void;
  muteAudio(): void;
  unmuteVideo(): void;
  muteVideo(): void;
  showBackgroundPanel(): void;
}

export const PreviewActionsPanel: FC<IPreviewActionsPanelProps> = observer((props) => {
  const { t } = useTranslation();
  const { presenter, videoInputPresenter, audioInputPresenter, audioOutputPresenter } =
    usePreviewActionsPanelPresenter({
      setCurrentAudioInput: props.setCurrentAudioInput,
      setCurrentAudioOutput: props.setCurrentAudioOutput,
      setCurrentVideoInput: props.setCurrentVideoInput,
      videoInputDeviceId: props.videoInputDeviceId,
      audioInputDeviceId: props.audioInputDeviceId,
      audioOutputDeviceId: props.audioOutputDeviceId,
      videoDevicesDisabled: props.videoPermissionDisabled,
      audioDevicesDisabled: props.audioPermissionDisabled,
    });

  const { isAudioMuted, isVideoMuted, unmuteAudio, muteAudio, unmuteVideo, muteVideo } = props;
  const audioButtonRef = useRef<HTMLButtonElement>(null);
  const videoButtonRef = useRef<HTMLButtonElement>(null);

  const videoMenuItems = useMemo(() => {
    const items: IPopoverMenuItem[] = [
      {
        key: 'title',
        title: t('calls_equipment_select_camera_header'),
        isDisabled: true,
        className: styles.menuTitle,
      },
    ];

    videoInputPresenter.devices.forEach((item) => {
      items.push({
        key: item.deviceId,
        title: item.label,
        onClick: () => presenter.setVideoInput(item.deviceId),
        iconSuffix:
          item.deviceId === videoInputPresenter.currentDeviceId ? (
            <div className={styles.icCheck}>
              <IconView Icon={IcDoneIcon} />
            </div>
          ) : undefined,
      });
    });

    if (props.canShowBackgroundPanel) {
      items.push({
        key: 'apply-delimeter',
        title: '',
        className: classNames(styles.menuTitle, styles.backgroundPanelDelimeter),
      });

      items.push({
        key: 'apply-background',
        icon: IcBackgroundEffectIcon,
        title: t('call_apply_background'),
        onClick: props.showBackgroundPanel,
      });
    }

    return items;
  }, [videoInputPresenter.devices, videoInputPresenter.currentDeviceId, presenter.setVideoInput]);

  const audioMenuItems = useMemo(() => {
    const items: IPopoverMenuItem[] = [
      {
        key: 'title',
        title: t('calls_equipment_select_microphone_header'),
        isDisabled: true,
        className: styles.menuTitle,
      },
    ];

    audioInputPresenter.devices.forEach((item) => {
      items.push({
        key: `${item.deviceId}-${item.kind}`,
        title: item.label,
        onClick: () => presenter.setAudioInput(item.deviceId),
        iconSuffix:
          item.deviceId === audioInputPresenter.currentDeviceId ? (
            <div className={styles.icCheck}>
              <IconView Icon={IcDoneIcon} />
            </div>
          ) : undefined,
      });
    });

    if (audioOutputPresenter.devices.length) {
      items.push({
        key: 'title-speaker',
        title: t('calls_equipment_select_speaker_header'),
        isDisabled: true,
        className: classNames(styles.menuTitle, styles.audioMenuTitle),
      });
    }

    audioOutputPresenter.devices.forEach((item) => {
      items.push({
        key: `${item.deviceId}-${item.kind}`,
        title: item.label,
        onClick: () => presenter.setAudioOutput(item.deviceId),
        iconSuffix:
          item.deviceId === audioOutputPresenter.currentDeviceId ? (
            <div className={styles.icCheck}>
              <IconView Icon={IcDoneIcon} />
            </div>
          ) : undefined,
      });
    });

    return items;
  }, [
    audioInputPresenter.devices,
    audioInputPresenter.currentDeviceId,
    presenter.setAudioInput,
    presenter.setAudioOutput,
    audioOutputPresenter.devices,
    audioOutputPresenter.currentDeviceId,
  ]);

  return (
    <div className={styles.container}>
      <div
        onClick={props.audioPermissionDisabled ? presenter.showAudioPermissionWarning : undefined}
        className={styles.buttonContainer}
      >
        <CallActionsPanelButton
          isActive={!isAudioMuted}
          isDisabled={props.audioPermissionDisabled}
          isArrowButtonPressed={presenter.isAudioPopoverOpened}
          setRef={audioButtonRef}
          Icon={isAudioMuted ? IcVoiceOff24Icon : IcVoiceOn24Icon}
          onClick={isAudioMuted ? unmuteAudio : muteAudio}
          onArrowClick={
            presenter.isAudioPopoverOpened
              ? presenter.closeAudioPopover
              : presenter.onAudioArrowClick
          }
          theme={isAudioMuted ? '12' : '13'}
        />
        {props.audioPermissionDisabled ? (
          <IconView
            Icon={IcReportFilled24Icon}
            size={IconViewSizes.Medium}
            className={styles.warningIcon}
          />
        ) : null}
      </div>
      <PopoverMenu
        className={styles.popover}
        isOpened={presenter.isAudioPopoverOpened}
        placement="top-end"
        menuItems={audioMenuItems}
        reference={audioButtonRef.current}
        onClose={presenter.closeAudioPopover}
        theme={'dark'}
      />
      <div
        onClick={props.videoPermissionDisabled ? presenter.showVideoPermissionWarning : undefined}
        className={styles.buttonContainer}
      >
        <CallActionsPanelButton
          isActive={!isVideoMuted}
          isDisabled={props.videoPermissionDisabled}
          isArrowButtonPressed={presenter.isVideoPopoverOpened}
          setRef={videoButtonRef}
          Icon={isVideoMuted ? IcVideoOff24Icon : IcVideoOn24Icon}
          onClick={isVideoMuted ? unmuteVideo : muteVideo}
          onArrowClick={
            presenter.isVideoPopoverOpened
              ? presenter.closeVideoPopover
              : presenter.onVideoArrowClick
          }
          theme={isVideoMuted ? '12' : '13'}
        />
        {props.videoPermissionDisabled ? (
          <IconView
            Icon={IcReportFilled24Icon}
            size={IconViewSizes.Medium}
            className={styles.warningIcon}
          />
        ) : null}
      </div>
      <PopoverMenu
        className={styles.popover}
        isOpened={presenter.isVideoPopoverOpened}
        placement="top-end"
        menuItems={videoMenuItems}
        reference={videoButtonRef.current}
        onClose={presenter.closeVideoPopover}
        theme={'dark'}
      />
    </div>
  );
});
